import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Source: docs/scenario_pages_plan.md EP04 — "Three Things Stacking Against Him".

export const statinRaj: Scenario = {
  slug: 'statin-raj',
  series: 'Drug Interactions',
  title: "The statin looked standard. His body got extra.",
  socialHook:
    "Raj, 51: a standard statin dose, new muscle aches, and a “natural” supplement. The clue was that three small factors were quietly turning one dose into too much.",
  meta: {
    description:
      "A fictional case study: Raj's muscle pain after starting atorvastatin was not random bad luck. SLCO1B1 slowed liver uptake, while daily grapefruit and red yeast rice amplified the exposure.",
  },

  persona: {
    name: 'Raj',
    age: 51,
    sex: 'Male',
    ancestry: 'South Asian',
    occupation: 'Secondary school headteacher',
    chiefConcern:
      "He started atorvastatin 40mg six weeks ago after his family doctor flagged elevated cholesterol. Within three weeks, his thighs and upper arms ached deeply. His CK is 420 U/L. He was told the dose was standard, but his body is acting as if the dose is not standard for him.",
    familyHistory: "Father: heart attack at 62. Paternal uncle: also on a statin, similar muscle complaints.",
  },

  clinicalPicture: {
    symptoms: [
      'Deep, persistent muscle aches — thighs, upper arms, occasionally calves',
      'Fatigue that started around the same time as the muscle symptoms',
      'Symptoms worse after exercise, better on rest days',
    ],
    labs: [
      { name: 'LDL ("bad") Cholesterol', value: '162 mg/dL', refRange: '<116 mg/dL', flag: 'high' },
      { name: 'CK (muscle enzyme)', value: '420 U/L', refRange: '<200 U/L', flag: 'high' },
    ],
    medications: ['Atorvastatin 40mg daily (started 6 weeks ago)'],
    supplements: ['Red yeast rice capsule (started independently, 1 month ago — labelled "natural cholesterol support")'],
    lifestyle: [
      'Morning routine: large grapefruit and mango smoothie, daily',
      'Exercise: gym 3x/week (weights + cardio)',
      'Diet: South Asian home cooking, moderate fat',
      'Non-smoker, alcohol rarely',
      'Sleep: 7 hours, generally good',
    ],
  },

  genetics: [
    {
      gene: 'SLCO1B1',
      variant: 'c.521T>C (Val174Ala)',
      rsid: 'rs4149056',
      genotype: 'TC (one copy of the risk variant)',
      plainMeaning:
        "Raj does not move atorvastatin into the liver as efficiently as average. SLCO1B1 helps explain why more drug stayed in his blood and reached his muscles.",
      technicalDetail:
        "SLCO1B1 encodes a transporter protein that pulls statins from the bloodstream into liver cells — where they act on cholesterol. The c.521T>C variant reduces that transporter's efficiency. With one copy, statin blood levels run roughly 1.5–2× higher than in people without the variant; with two copies it can reach 3–4×. Higher circulating levels mean more statin reaching muscle tissue, which is where statin-related myopathy originates. This is the most clinically actionable pharmacogenetic variant for statin tolerance — and it's rarely tested before prescribing.",
    },
    {
      gene: 'CYP3A4',
      variant: 'Normal metabolizer (no variants)',
      rsid: 'rs35599367 / rs4986910',
      genotype: 'Wild type',
      plainMeaning:
        "His statin-clearing enzyme is normal, but grapefruit can block it for the day. That makes his daily smoothie a real part of the case.",
      technicalDetail:
        "CYP3A4 is the liver enzyme responsible for breaking down atorvastatin (and many other drugs). Raj has no genetic variants here — but grapefruit contains furanocoumarins that block CYP3A4 for up to 24 hours. Even with normal CYP3A4 genetics, a daily grapefruit effectively converts a normal metabolizer into a poor one for that dose window. Combined with his SLCO1B1 variant, his statin isn't clearing properly from two directions at once.",
    },
    {
      gene: 'CoQ10 (indirect — HMGCR pathway)',
      variant: 'Not directly tested',
      rsid: 'N/A',
      genotype: 'Context: statins deplete CoQ10',
      plainMeaning:
        "Higher statin exposure can make muscle energy problems more likely. CoQ10 is not a genetic result here, but it helps explain the ache pattern.",
      technicalDetail:
        "Statins block HMGCR — the same pathway the body uses to synthesize coenzyme Q10 (CoQ10), which muscles require for energy production. This depletion is a class effect of statins, but people with higher baseline statin exposure (like Raj, due to SLCO1B1) experience more pronounced depletion. CoQ10 isn't genetically tested here, but it's mechanistically relevant: his muscle cells are receiving more statin than intended and producing less cellular energy as a result.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "The prescription looked routine",
      body: "Raj's father had a heart attack at 62. When Raj's LDL came back at 162 mg/dL, his family doctor started atorvastatin 40mg. The plan made sense: lower the cholesterol, lower the risk. Three weeks later, Raj's thighs ached every morning and gym recovery felt unusually hard. The dose was standard. The symptoms were not.",
    },
    {
      type: 'reveal',
      heading: "The first clue was where the drug got stuck",
      body: "Atorvastatin is supposed to move from the blood into the liver, where it lowers cholesterol. Raj's SLCO1B1 variant makes that transport less efficient. More drug can stay in the bloodstream, and more can reach muscle. Statin-related muscle problems are often exposure problems. Raj was not simply unlucky; the standard dose produced a higher-than-standard exposure for him.",
    },
    {
      type: 'reveal',
      heading: "Then two everyday choices raised the exposure again",
      body: "Raj's morning grapefruit smoothie blocks CYP3A4, the enzyme that helps clear atorvastatin, for much of the day. Then he added red yeast rice, a “natural cholesterol support” supplement that contains monacolin K, chemically identical to lovastatin. Without realizing it, he was combining a statin prescription, a statin-like supplement, and a food interaction. The SLCO1B1 variant set the stage. Grapefruit and red yeast rice made the dose feel much larger.",
    },
    {
      type: 'action',
      heading: "Five fixes that lower the extra exposure",
      items: [
        "Stop red yeast rice and tell his family doctor. It contains monacolin K, chemically identical to lovastatin, so taking it with atorvastatin combines statin exposures.",
        "Remove grapefruit from the daily smoothie. Grapefruit can block atorvastatin clearance for much of the day, so timing it away from the pill is not enough.",
        "Discuss SLCO1B1-guided statin choice. CPIC guidelines support lower-dose or alternative statins for people with this variant; rosuvastatin and pravastatin are less affected than atorvastatin.",
        "Ask whether a CoQ10 trial is reasonable. Evidence is mixed, but for statin-related muscle symptoms, a 6–8 week trial at 100–200mg/day is worth discussing.",
        "Recheck CK after removing red yeast rice and grapefruit. His CK is elevated but not in the danger zone; a repeat test can show whether exposure reduction is working.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'heatmap' as const,
      heading: "Six weeks of symptoms mapped",
      caption:
        "The pattern tells the story: symptoms appeared after the statin started, peaked when red yeast rice and grapefruit stacked on top, then began to clear after the amplifiers were removed.",
      weekLabels: [
        'Week 1 — Pre-statin',
        'Week 2 — Statin started',
        'Week 3 — Red yeast rice added',
        'Week 4 — Peak symptoms',
        'Week 5 — Red yeast rice & grapefruit stopped',
        'Week 6 — Recovery begins',
      ],
      rows: [
        {
          symptom: 'Muscle aches',
          days: [0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 2, 2, 2, 2, 2, 3, 3, 3, 4, 4, 4, 5, 5, 4, 4, 3, 3, 3, 2, 2, 2, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0],
        },
        {
          symptom: 'Fatigue',
          days: [0, 0, 0, 0, 0, 0, 0, 1, 1, 2, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 5, 4, 4, 3, 3, 3, 2, 2, 2, 2, 2, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
        },
        {
          symptom: 'Post-exercise pain',
          days: [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 2, 2, 2, 3, 3, 3, 4, 4, 4, 5, 5, 5, 4, 4, 4, 3, 3, 3, 2, 2, 2, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0],
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'statin-raj',
    label: 'Explore your statin & drug interaction genetics in Coach',
  },
}
